import styled from "styled-components";

// ---------------------------------------------------------------------------
// Accent / brand token
// ---------------------------------------------------------------------------
const NAVY = "#005e9e";
const NAVY_DARK = "#004a7f";
const NAVY_LIGHT = "#e8f2fa";

// ---------------------------------------------------------------------------
// Topbar
// ---------------------------------------------------------------------------

export const Topbar = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 0 14px;
	height: 48px;
	background: #fff;
	border-bottom: 1px solid #e3e8ef;
	flex-shrink: 0;
	user-select: none;
`;

export const TopbarTitle = styled.span`
	font-size: 14px;
	font-weight: 700;
	color: #1e293b;
	margin-right: 4px;
`;

export const TopbarSpacer = styled.div`
	flex: 1;
`;

// ---------------------------------------------------------------------------
// Segmented toggle (Sửa / Xem)
// ---------------------------------------------------------------------------

export const SegToggle = styled.div`
	display: flex;
	background: #f1f5f9;
	border-radius: 8px;
	padding: 3px;
	gap: 2px;
`;

interface SegBtnProps {
	active?: boolean;
}

export const SegBtn = styled.button<SegBtnProps>`
	border: none;
	cursor: pointer;
	font-size: 13px;
	font-weight: 600;
	padding: 5px 14px;
	border-radius: 6px;
	transition: background 0.15s, color 0.15s;
	background: ${({ active }) => (active ? "#fff" : "transparent")};
	color: ${({ active }) => (active ? NAVY : "#64748b")};
	box-shadow: ${({ active }) => (active ? "0 1px 3px rgba(0,0,0,0.10)" : "none")};

	&:hover:not(:disabled) {
		color: ${NAVY};
	}
`;

// ---------------------------------------------------------------------------
// Icon buttons (Undo, Redo, Dark, Sample)
// ---------------------------------------------------------------------------

interface IconBtnProps {
	active?: boolean;
}

export const IconBtn = styled.button<IconBtnProps>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border: none;
	border-radius: 7px;
	cursor: pointer;
	background: ${({ active }) => (active ? NAVY_LIGHT : "transparent")};
	color: ${({ active }) => (active ? NAVY : "#64748b")};
	font-size: 16px;
	transition: background 0.15s, color 0.15s;

	&:disabled {
		opacity: 0.35;
		cursor: default;
	}

	&:hover:not(:disabled) {
		background: ${NAVY_LIGHT};
		color: ${NAVY};
	}
`;

// ---------------------------------------------------------------------------
// Badge (e.g. missing-vars count)
// ---------------------------------------------------------------------------

export const Badge = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 18px;
	height: 18px;
	padding: 0 5px;
	border-radius: 999px;
	background: #ef4444;
	color: #fff;
	font-size: 11px;
	font-weight: 700;
	line-height: 1;
	margin-left: 2px;
`;

// ---------------------------------------------------------------------------
// Presets button (labeled — the "start from a template" action)
// ---------------------------------------------------------------------------

export const PresetBtn = styled.button<IconBtnProps>`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	height: 32px;
	padding: 0 14px;
	border: 1px solid ${NAVY};
	border-radius: 8px;
	cursor: pointer;
	font-size: 13px;
	font-weight: 700;
	white-space: nowrap;
	background: ${({ active }) => (active ? NAVY : NAVY_LIGHT)};
	color: ${({ active }) => (active ? "#fff" : NAVY)};
	transition: background 0.15s, color 0.15s;

	&:hover:not(:disabled) {
		background: ${NAVY};
		color: #fff;
	}
`;

// ---------------------------------------------------------------------------
// Save button
// ---------------------------------------------------------------------------

export const SaveBtn = styled.button`
	display: flex;
	align-items: center;
	gap: 5px;
	border: none;
	cursor: pointer;
	font-size: 13px;
	font-weight: 700;
	padding: 7px 18px;
	border-radius: 8px;
	background: ${NAVY};
	color: #fff;
	transition: background 0.15s;
	margin-left: 6px;

	&:hover {
		background: ${NAVY_DARK};
	}
`;

// ---------------------------------------------------------------------------
// 3-pane shell
// ---------------------------------------------------------------------------

export const ShellWrap = styled.div`
	display: flex;
	flex-direction: column;
	/* flex:1 + min-height:0 let this shell fill the remaining space in the
	   parent flex column (StyledUpdateContentWrap) after the fixed-height
	   template-name / subject rows. height:100% is kept as a fallback for
	   contexts where ShellWrap is the only child of an explicit-height parent. */
	flex: 1;
	min-height: 0;
	height: 100%;
	background: #f8fafc;
	font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
`;

export const PaneRow = styled.div`
	display: flex;
	flex: 1;
	min-height: 0;
	overflow: hidden;

	/* On narrow screens the three panes can no longer all keep comfortable
	   widths. Rather than crushing the center canvas, allow the whole row to
	   scroll horizontally so every pane keeps a usable minimum width. */
	@media (max-width: 860px) {
		overflow-x: auto;
	}
`;

// ---------------------------------------------------------------------------
// Side panels (Palette left, Inspector right)
// ---------------------------------------------------------------------------

export const SidePanel = styled.div`
	width: 220px;
	flex-shrink: 0;
	background: #fff;
	border-right: 1px solid #e3e8ef;
	overflow-y: auto;
	padding: 12px;
	font-size: 13px;
	color: #64748b;

	&:last-child {
		border-right: none;
		border-left: 1px solid #e3e8ef;
	}

	/* Shrink the side panels on smaller laptops so the center canvas keeps
	   enough room to edit comfortably. Below 860px the PaneRow scrolls
	   horizontally and panels keep this minimum usable width. */
	@media (max-width: 1280px) {
		width: 196px;
	}
	@media (max-width: 1080px) {
		width: 176px;
	}
	@media (max-width: 860px) {
		width: 168px;
	}
`;

// ---------------------------------------------------------------------------
// Center canvas area
// ---------------------------------------------------------------------------

export const CanvasArea = styled.div`
	flex: 1;
	min-width: 0;
	overflow-y: auto;
	background: #e7ebf1;
	display: flex;
	justify-content: center;
	align-items: flex-start;
	padding: 26px 20px;

	/* Keep a usable editing width on small screens — combined with PaneRow's
	   horizontal scroll this prevents the canvas from being squeezed to nothing. */
	@media (max-width: 860px) {
		min-width: 420px;
		padding: 20px 12px;
	}
`;

export const CanvasAreaDark = styled(CanvasArea)`
	background: #0b1018;
`;
